import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";
import { Badge } from "../components/ui/badge";
import { MapPin, Phone, Mail, Shield, Star, Building2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import DealerReviews, { DealerRatingSummary } from "../components/DealerReviews";
import WorkshopReviewsSection from "../components/WorkshopReviewsSection";

export default function DealerProfilePage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [dealer, setDealer] = useState(null);
    const [listings, setListings] = useState([]);
    const [favIds, setFavIds] = useState([]);
    const { user } = useAuth();

    const [reviewSummary, setReviewSummary] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        (async () => {
            // Jul-2026 (User-Meldung): bei unbekannter ID nicht endlos laden
            let u, l;
            try {
                [u, l] = await Promise.all([
                    api.get(`/users/${id}`),
                    api.get(`/listings?owner_id=${id}`),
                ]);
            } catch (_) {
                setNotFound(true);
                return;
            }
            setDealer(u.data);
            setListings(l.data);
            try {
                const r = await api.get(`/dealers/${id}/reviews?limit=1`);
                setReviewSummary(r.data.summary);
            } catch (_) { /* ignore */ }
            if (user && user !== false) {
                try {
                    const ids = await api.get("/favorites/ids");
                    setFavIds(ids.data);
                } catch (e) {
                    console.error("Failed to load favorite ids", e);
                }
            }
        })();
    }, [id, user]);

    if (notFound) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-16 text-center" data-testid="dealer-profile-not-found">
                <h1 className="font-display text-2xl font-black mb-2">Händler nicht gefunden</h1>
                <p className="text-gray-500 text-sm">Der Link ist ungültig oder das Profil existiert nicht mehr.</p>
            </div>
        );
    }
    if (!dealer) return <div className="max-w-7xl mx-auto px-4 py-12">{t("dealer_profile.loading")}</div>;
    const isB2B = dealer.role === "b2b";

    const toggleFav = async (lid) => {
        if (!user || user === false) return;
        const { data } = await api.post(`/favorites/${lid}`);
        setFavIds((prev) => data.favorited ? [...prev, lid] : prev.filter((x) => x !== lid));
    };

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="dealer-profile-page">
            <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-10 mb-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-2 yellow-stripe" />
                <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className={`w-24 h-24 flex-shrink-0 rounded-sm flex items-center justify-center font-display font-black text-4xl ${isB2B ? "bg-[#111827] text-[#FFCC00]" : "bg-[#FFCC00] text-[#111827]"}`}>
                        {(dealer.company_name || dealer.name || "?").charAt(0)}
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <Badge className={isB2B ? "bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide font-bold rounded-sm" : "bg-gray-200 text-gray-800 hover:bg-gray-200 uppercase tracking-wide font-bold rounded-sm"}>
                                {isB2B ? t("dealers.badge_dealer") : t("dealers.badge_private")}
                            </Badge>
                            {dealer.verified && <Badge className="bg-green-100 text-green-800 hover:bg-green-100 gap-1 rounded-sm"><Shield className="w-3 h-3" /> {t("dealer_profile.verified")}</Badge>}
                        </div>
                        <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight mb-2" data-testid="dealer-name">{dealer.company_name || dealer.name}</h1>
                        {dealer.rating && (
                            <div className="flex items-center gap-1 mb-3">
                                <Star className="w-4 h-4 fill-[#FFCC00] text-[#FFCC00]" />
                                <span className="font-mono-num font-bold">{dealer.rating}</span>
                                <span className="text-sm text-gray-500">/ 5.0</span>
                            </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700 mt-4">
                            {dealer.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /> {dealer.address}</div>}
                            {dealer.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /> {dealer.phone}</div>}
                            {dealer.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /> {dealer.email}</div>}
                            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-gray-400" /> {t("dealers.listings_count", { count: dealer.listings_count })}</div>
                        </div>
                        {isB2B && dealer.ansprechpartner_employee_id && (dealer.contact_first_name || dealer.contact_last_name) && (
                            <div className="flex items-center gap-3 mt-5 p-3 bg-gray-50 border border-gray-200 rounded-sm w-fit" data-testid="dealer-ansprechpartner-block">
                                {dealer.ansprechpartner_photo_url ? (
                                    <img src={dealer.ansprechpartner_photo_url} alt={`${dealer.contact_first_name || ""} ${dealer.contact_last_name || ""}`} className="w-12 h-12 rounded-full object-cover border border-gray-200" data-testid="dealer-ansprechpartner-avatar" />
                                ) : (
                                    <span className="w-12 h-12 rounded-full bg-[#111827] text-[#FFCC00] inline-flex items-center justify-center font-display font-black text-sm" data-testid="dealer-ansprechpartner-avatar">
                                        {`${dealer.contact_first_name || ""} ${dealer.contact_last_name || ""}`.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                                    </span>
                                )}
                                <div>
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Ihr Ansprechpartner</div>
                                    <div className="font-bold text-[#111827]" data-testid="dealer-ansprechpartner-name">{`${dealer.contact_first_name || ""} ${dealer.contact_last_name || ""}`.trim()}</div>
                                    {dealer.contact_email && <div className="text-xs text-gray-500">{dealer.contact_email}</div>}
                                </div>
                            </div>
                        )}
                        {(dealer.sell_regional || dealer.sell_eu || dealer.sell_international) && (
                            <div className="flex flex-wrap gap-2 mt-4" data-testid="reach-badges">
                                {dealer.sell_regional && (
                                    <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 rounded-sm uppercase tracking-wider text-[10px] font-bold" data-testid="badge-regional">
                                        {t("dealer_profile.reach_regional")}
                                    </Badge>
                                )}
                                {dealer.sell_eu && (
                                    <Badge className="bg-indigo-100 text-indigo-800 hover:bg-indigo-100 rounded-sm uppercase tracking-wider text-[10px] font-bold" data-testid="badge-eu">
                                        {t("dealer_profile.reach_eu")}
                                    </Badge>
                                )}
                                {dealer.sell_international && (
                                    <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100 rounded-sm uppercase tracking-wider text-[10px] font-bold" data-testid="badge-international">
                                        {t("dealer_profile.reach_international")}
                                    </Badge>
                                )}
                            </div>
                        )}
                        <div className="mt-3" data-testid="header-rating-summary">
                            <DealerRatingSummary summary={reviewSummary} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-end justify-between mb-6">
                <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight">{t("dealer_profile.current_listings")}</h2>
                <div className="text-sm text-gray-500">{t("dealer_profile.vehicles_count", { count: listings.length })}</div>
            </div>

            {listings.length === 0 ? (
                <div className="bg-white border border-dashed border-gray-300 p-12 text-center rounded-sm">{t("dealer_profile.no_listings")}</div>
            ) : (
                <div className="space-y-4">
                    {listings.map((l) => (
                        <ListingCard key={l.id} listing={l} onToggleFavorite={toggleFav} isFavorited={favIds.includes(l.id)} />
                    ))}
                </div>
            )}

            <DealerReviews dealerId={id} dealerName={dealer.company_name || dealer.name} />
            {isB2B && <WorkshopReviewsSection dealerId={id} />}
        </div>
    );
}
